import { useCallback } from 'react';
import { tracker } from '@kueski-dev/web-observability';
import { useConfig } from 'Contexts/Config';
import { HomeEvents, trackEvent } from '@kueski-analytics/events/home';
import { TrackerMediator } from '@kueski-analytics/utils/TrackerMediator';
import { EVENT_FEATURE_FLAGS } from '@kueski-analytics/utils/EventFeatureFlags';
import useCommonHomeEventFields from './useCommonHomeEventFields';

export default function useHomeEventTracker() {
  const { commonFields, isLoading } = useCommonHomeEventFields();
  const { features } = useConfig();
  const track: typeof tracker['track'] = (event, options) => {
    if (!features.observabilityAuroraHome) return Promise.resolve();

    const mediator = new TrackerMediator(EVENT_FEATURE_FLAGS.home);
    return mediator
      .addNewTracker(() => (
        tracker.track(event, {
          ...options,
          customAttributes: {
            ...commonFields,
            ...options?.customAttributes,
          },
        })
      ))
      .track();
  };

  return { isLoading, track };
}

type Events = Omit<HomeEvents, 'start_session' | 'home_visited'>; // exclude non normalized events

// DEPRECATED:
useHomeEventTracker.deprecated = <Event extends keyof Events>(event: Event) => {
  const { commonFields, isLoading } = useCommonHomeEventFields();

  type ExtraFields = Omit<Events[Event], keyof typeof commonFields>;

  const track = useCallback(async (
    ...args: ExtraFields extends Record<string, never> ? [] : [extraFields: ExtraFields]
  ) => {
    if (isLoading) throw new Error('Event data is not ready');
    const extraFields = args[0] || {};

    const allEventAttributes = {
      ...commonFields,
      ...extraFields,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;

    const mediator = new TrackerMediator(EVENT_FEATURE_FLAGS.home);
    return mediator
      .addOldTracker(() => trackEvent(event, allEventAttributes))
      .addNewTracker(() => undefined)
      .track();
  }, [commonFields, isLoading]);

  return [track, isLoading] as const;
};
